import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
        warning:
          "border-transparent bg-amber-100 text-amber-800 hover:bg-amber-200",
        success:
          "border-transparent bg-green-100 text-green-800 hover:bg-green-200",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  /**
   * テキストを切り捨てる最大文字数
   * 指定された文字数を超えると「...」が追加されます
   */
  maxLength?: number;
  /**
   * 切り捨てられたテキストをホバー時にツールチップで表示するかどうか
   */
  showTooltip?: boolean;
}

function Badge({
  className,
  variant,
  maxLength,
  showTooltip = true,
  children,
  ...props
}: BadgeProps) {
  // childrenがstring型でない場合は何もせずそのまま表示
  if (typeof children !== "string" || !maxLength) {
    return (
      <div className={cn(badgeVariants({ variant }), className)} {...props}>
        {children}
      </div>
    );
  }

  // テキストを切り捨てる処理
  const text = children;
  const isTruncated = text.length > maxLength;
  const displayText = isTruncated ? `${text.substring(0, maxLength)}...` : text;

  // 切り捨てがある場合かつshowTooltipがtrueの場合にtitleを追加
  const tooltipProps = isTruncated && showTooltip ? { title: text } : {};

  return (
    <div
      className={cn(badgeVariants({ variant }), className)}
      {...tooltipProps}
      {...props}
    >
      {displayText}
    </div>
  );
}

export { Badge, badgeVariants };
